import { Box, Skeleton, Typography } from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { abyssColors } from '@/theme/colors';
import { CustomTooltip } from '@/components/shared/GenericBarChart/GenericBarChart';
import { useRiskRemediationData } from '@/hooks/useRiskRemediationData';
import { useEffect, useMemo, useState } from 'react';
import { Analytics } from '@/__generated__/graphql';
import {
  LegendContainer,
  LegendDot,
  LegendItem,
  LegendLabel,
} from '@/components/Structures/PlatformConditionCharts/styles';
import { LegendItemConfig } from '@/components/Structures/PlatformConditionCharts/PlatformConditionCharts';
import { StyledLegendForCombinedRisk } from '@/components/shared/GenericBarChart/style';

export type Metric = Record<
  string,
  {
    label: string;
    color: string;
    order?: number;
    format?: string;
    strokeWidth?: number;
    dotRadius?: number;
    dotColor?: string;
    isInternal?: boolean;
  }
>;

export const RiskRemidiationChart = ({ activeStructureIds }: { activeStructureIds: string[] }) => {
  const [legendData, setLegendData] = useState({});
  const [lineChartLabels, setLineChartLabels] = useState<string[]>([]);
  const {
    data: riskRemidiationData,
    displayConfig: graphDisplayConfig,
    loading,
  } = useRiskRemediationData(activeStructureIds);

  const rootFontSize = getComputedStyle(document.documentElement);
  const parsed = parseFloat(rootFontSize.fontSize);
  const graphHeight = 40 * parsed;

  const legendItems: LegendItemConfig[] = useMemo(() => {
    if (!graphDisplayConfig?.metrics) return [];
    return (Object.entries(graphDisplayConfig?.metrics as Metric) || [])
      .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
      .map(([key, value]) => ({
        key: key,
        name: value?.label,
        color: value?.color,
        isInternal: value?.isInternal ?? false,
      }));
  }, [graphDisplayConfig]);

  const renderBarCharts = (metrics: Metric | undefined) => {
    if (!metrics) return null;
    return Object.entries(metrics)
      .filter(([, value]) => value?.format?.toUpperCase() === 'BAR')
      .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
      .map(([key, value]) => {
        return (
          <Bar
            key={key}
            dataKey={key}
            fill={value?.color}
            xAxisId={graphDisplayConfig?.xAxis?.at(0)?.field}
            radius={graphDisplayConfig?.barRadius ?? 2}
            name={value?.label}
          />
        );
      });
  };

  const renderLineCharts = (metrics: Metric | undefined) => {
    if (!metrics) return null;
    return Object.entries(metrics)
      .filter(([, value]) => value?.format?.toUpperCase() === 'LINE')
      .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
      .map(([key, value]) => {
        return (
          <Line
            key={key}
            dataKey={key}
            stroke={value?.color}
            strokeWidth={value?.strokeWidth}
            dot={{
              fill: value?.dotColor,
              r: value?.dotRadius,
            }}
            xAxisId={graphDisplayConfig?.xAxis?.at(0)?.field}
            name={value?.label}
          />
        );
      });
  };

  const renderXAxis = (xAxis: Analytics['displayConfig']['xAxis']) => {
    if (!xAxis) return null;
    const axisToReturn = xAxis.map((axis: Analytics['displayConfig']['xAxis'][number]) => {
      const fontSize = `${axis?.tickFontSize / 10}rem`;
      return (
        <XAxis
          dataKey={axis?.field}
          domain={['auto', 'dataMax']}
          allowDataOverflow
          interval={0}
          xAxisId={axis?.field}
          tick={{
            fontSize: fontSize,
            color: axis?.tickColor,
            dy: axis?.dy,
            fontWeight: axis?.tickFontWeight,
          }}
          tickLine={false}
          axisLine={false}
          opacity={0.8}
          hide={axis?.hideAxis ?? false}
        />
      );
    });
    return axisToReturn;
  };

  const renderYAxis = (yAxis: Analytics['displayConfig']['yAxis']) => {
    if (!yAxis) return null;
    const dy = 0;
    return yAxis.map((axis: Analytics['displayConfig']['yAxis'][number]) => {
      const fontSize = `${axis?.tickFontSize / 10}rem`;
      return (
        <YAxis
          tick={{ fontSize: fontSize, color: axis?.tickColor, fontWeight: axis?.tickFontWeight }}
          opacity={0.8}
          tickLine={false}
          axisLine={false}
          tickCount={6}
          label={{
            value: axis?.label ?? 'Score',
            angle: -90,
            position: 'insideBottomRight',
            dy: dy + 20,
            fontSize: `${axis.labelFontSize / 10}rem`,
            fontWeight: axis?.labelFontWeight,
          }}
          domain={[0, 'auto']}
          allowDataOverflow
          dataKey={axis?.field}
          hide={axis?.hideAxis ?? false}
        />
      );
    });
  };

  const renderReferenceArea = () => {
    const xAxisToRenderReferenceArea = (
      <XAxis
        dataKey="index"
        type="number"
        xAxisId="reference-area"
        domain={['0', 'dataMax + 1']}
        hide
      />
    );
    const referencesArea = riskRemidiationData?.map(
      (item: Analytics['data'][number], index: number) => {
        return (
          <ReferenceArea
            key={index}
            x1={index + 0.15}
            x2={index + 1 - 0.15}
            y1={0}
            y2={item?.combined_score}
            stroke={graphDisplayConfig?.referenceArea?.strokeColor ?? abyssColors.primary[200]}
            strokeWidth={graphDisplayConfig?.referenceArea?.strokeWidth ?? 2}
            strokeOpacity={graphDisplayConfig?.referenceArea?.strokeOpacity ?? 0.3}
            xAxisId="reference-area"
            fill={graphDisplayConfig?.referenceArea?.color ?? abyssColors.neutral[100]}
            radius={graphDisplayConfig?.referenceArea?.radius ?? 2}
          />
        );
      }
    );
    return [...referencesArea, xAxisToRenderReferenceArea];
  };

  useEffect(() => {
    if (!graphDisplayConfig?.metrics) return;
    setLegendData(
      Object.entries(graphDisplayConfig.metrics as Metric)
        .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
        .reduce((acc, [, value]) => {
          acc[value.label] = value.color;
          return acc;
        }, {} as { [key: string]: string })
    );
    setLineChartLabels(
      Object.entries(graphDisplayConfig.metrics as Metric)
        .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
        .filter(([, value]) => value?.format?.toUpperCase() === 'LINE')
        .map(([key]) => graphDisplayConfig.metrics[key].label)
    );
  }, [graphDisplayConfig]);

  return (
    riskRemidiationData?.length > 0 && (
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          padding: '2.4rem',
          marginTop: '1.6rem',
          border: `1px solid ${abyssColors.primary[200]}`,
          borderRadius: '8px',
        }}
      >
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={graphHeight}
            sx={{ marginBottom: '10px' }}
          />
        ) : (
          <>
            <Typography
              sx={{
                fontSize: `${
                  graphDisplayConfig?.title?.fontSize
                    ? graphDisplayConfig?.title?.fontSize / 10
                    : 1.6
                }rem !important`,
                fontWeight: `${graphDisplayConfig?.title?.fontWeight ?? '700'} !important`,
                color: graphDisplayConfig?.title?.color ?? '#224063',
                marginLeft: '0.6rem',
                marginBottom: '2.6rem',
              }}
            >
              {graphDisplayConfig?.title?.label ?? 'Risk remediation trend'}
            </Typography>
            <ResponsiveContainer width="100%" height={graphHeight}>
              <ComposedChart data={riskRemidiationData} barCategoryGap={'20%'}>
                {renderXAxis(graphDisplayConfig?.xAxis)}
                {renderYAxis(graphDisplayConfig?.yAxis)}
                {graphDisplayConfig?.referenceArea?.isVisible && renderReferenceArea()}
                <Tooltip
                  content={
                    <CustomTooltip
                      active
                      legendData={legendData}
                      riskRemidiationDataChart
                      lineChartLabels={lineChartLabels}
                    />
                  }
                />
                <CartesianGrid vertical={false} color={abyssColors.primary[200]} opacity={0.2} />
                {renderBarCharts(graphDisplayConfig?.metrics as Metric)}
                {renderLineCharts(graphDisplayConfig?.metrics as Metric)}
              </ComposedChart>
            </ResponsiveContainer>
            <LegendContainer>
              {legendItems.map((item) => {
                return (
                  <LegendItem key={item.key}>
                    {lineChartLabels.includes(item?.name) ? (
                      <StyledLegendForCombinedRisk bgcolor={item?.color} />
                    ) : (
                      <LegendDot color={item.color} />
                    )}
                    <LegendLabel>{item.name || item.key}</LegendLabel>
                  </LegendItem>
                );
              })}
            </LegendContainer>
          </>
        )}
      </Box>
    )
  );
};
